import {
  toRef,
  watch,
} from 'vue'
import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'

import useQueries from '@/composables/useQueries'

/*
TODO:
when moving this to UI Kit, use "useRouter" & "useRoute" instead of "useRouterStore".

With vue-router ^3.6.0 (Vue2), they are exported from the 'vue-router/composables' submodule.
With vue-router ^4.0.0 (Vue3), they are exported from the 'vue-router' module.

So those modules should be added as peerDependency/optionnalDependency.
We need to find a way to conditionally import one or the other.

See:
See https://github.com/vuejs/vue-router/blob/dev/CHANGELOG.md#360-2022-08-22
https://github.com/vuejs/router/blob/main/packages/router/CHANGELOG.md

Otherwise, we can create some kind of "setRouterComposable" & "setRouteComposable" methods that
can be called during initialization of the app (in main.js for instance) to store the composables
provided as argument in a variable that can be imported by this composable and others.
*/

/**
 * @typedef UseQuerySyncOptions
 * @property {*} initial - Initial value or initial ref to use
 * @property {boolean} push - Push mode,
 * pushes the query into a new history entry instead of replacing it
 * @property {function} parse - Parsing function to convert from query to ref value
 * @property {function} serialize - Serialization function to convert from ref value to query
 */

/**
 * Sync a query parameter to a ref
 * @param {*} key - Key of the query value
 * @param {*} options - Sync options ("initial" value or ref, "push" mode)
 * @returns {import('vue').Ref} Editable ref that updates the query and is updated by the query
 */
function useQuerySync(key, {
  initial,
  parse,
  serialize,
  bidirectional = true,
} = {}) {
  const queries = useQueries()
  const queryRef = toRef(queries, key)
  const valueRef = useRef(initial)
  const bidirectionalRef = useRef(bidirectional)

  let init = false

  // When the query changes, update the ref
  watch(queryRef, (newQuery) => {
    // If init was already done OR init has not been done and the query is not undefined
    if (init || typeof newQuery !== 'undefined') {
      init = true
      const parsed = typeof parse === 'function' ? parse(newQuery) : newQuery
      if (parsed !== valueRef.value) {
        valueRef.value = parsed
      }
    }
  }, { immediate: true })

  // When the ref changes, update the query
  watch(valueRef, (newValue) => {
    if (bidirectionalRef.value) {
      const serialized = typeof serialize === 'function' ? serialize(newValue) : newValue
      if (serialized !== queryRef.value) {
        queryRef.value = serialized
      }
    }
  }, { immediate: true })

  return valueRef
}

export default useQuerySync
